import { api } from '../../api/client';
import type { SdmsCellChange } from './SdmsUniverSheet';   // type-only — erased at build, no chunk pull
import type { SdmsImportValue } from './sdmsImportSequence';

// ── server DTOs (match Application/Common/Interfaces/IQcBatchReader.cs +
//    Application/Analysis/QcBatch/QcSequenceBuilder.cs) ──────────────────────
export interface QcJob        { jobOid: string; jobID: string | null; receivedDate: string | null; dueDate: string | null; tat: string | null; sampleCount: number }
// Fields after labwareID are populated only for the Others-tab "Selected"
// summary grid (AI_Analysis_QcBatch_LinkedInstruments_SP) — the header-field
// picker and Add-popup catalog don't select them, so they're undefined there.
export interface QcInstrument {
  instrumentOid: string; instrumentName: string | null; labwareName: string | null; labwareID?: string | null;
  manufacturer?: string | null; expirationDate?: string | null; departmentUsedName?: string | null;
  active?: boolean; maintenanceFrequency?: string | null; departmentNames?: string | null;
  categoryName?: string | null; retiredDate?: string | null;
}
export interface QcTemplate   { templateID: number; templateName: string | null }
export interface QcPrepBatch  { prepBatchOid: string; prepBatchID: string | null; createdDate: string | null; createdBy: string | null; roomTemp: string | null; humidity: string | null; instrumentOid: string | null; instrumentName: string | null }
export interface QcTypeOption { qcTypeOid: string; qcTypeName: string | null; sequenceOrder: number | null; sort: number | null; qcSource: string | null; qcRole: string | null; qcRootRole: string | null; panelVisible: boolean; hasActiveTp: boolean }

// Reagents (Others tab) — legacy ReagentLink_Execute parity.
export interface QcLinkedReagent {
  oid: string; reagentID: string | null; reagentName: string | null; prepType: string | null;
  expiredDate: string | null; vendorLT: string | null; preparedBy: string | null;
  datePrepared: string | null; dateReceived: string | null; vendorName: string | null;
  /** dbo.Reagent.Vendor Oid — Copy From Previous re-links the reagent WITH its vendor (OG parity). */
  vendorOid?: string | null;
  // True only for an LT (not LLT) reagent with an uploaded vendor cert on file —
  // gates whether the grid shows a clickable COA link (legacy hides it otherwise).
  hasCoa: boolean;
}
export interface QcAvailableReagent {
  reagentID: string | null; reagentName: string | null; prepType: string | null;
  expiryDate: string | null; test: string | null; datePrepared: string | null;
  preparedBy: string | null; division: string | null; department: string | null;
  createdDate: string | null; dateReceived: string | null;
  /** Vendor Oid — saved into dbo.Reagent.Vendor on link / Create (OG copies it, :12780). */
  vendor: string | null; vendorLT: string | null;
  /** Vendor display name (AI-only extra catalog column) — for the Selected Reagents grid. */
  vendorName?: string | null;
}

export interface QcSample {
  sampleLogInOid: string;
  sysSampleCode:  string | null;
  jobID:          string | null;
  component:      string | null;
  clientSampleID: string | null;
  collectDate:    string | null;
  sampleNo:       number;
  // legacy QC Batch grid columns (post-Load, pre-Sort)
  dilutionCount:  number;
  finalVolMl:     string | null;
  // Analyst-entered pre-Sort (no source column); sent back on Sort so the built
  // sequence retains it — set from the current grid row in buildSequence().
  sampleAmount:   string | null;
  excludedReport: boolean;
  units:          string | null;
  sampleName:     string | null;
  siteName:       string | null;
  samplingAddress: string | null;
  dateCreated:    string | null;
  dateReceived:   string | null;
  dateCollected:  string | null;
  client:         string | null;
  projectID:      string | null;
  projectName:    string | null;
  holdTime:       string | null;
  // true when this sample still needs sample-prep before it can be sorted into a
  // QC batch (legacy prep-batch gate) — surfaced as a Sort-time validation.
  requiresPrep:   boolean;
}

export interface QcSeqRow {
  // The persisted QCBatchSequence.Oid for a row read from a SAVED batch (null for
  // a freshly-built or freshly-added row) — round-tripped so the reconcile-on-Save
  // update (A1) can diff existing vs added rows. Survives Add/Remove/Move/Dilution
  // (the server builders shallow-copy the list + mutate existing instances in place).
  seqOid?:     string | null;
  kind:        'sample' | 'qc' | 'dilution';
  qcType:      string;
  qcTypeOid:   string | null;
  sort:        number;
  runno:       number;
  code:        string;
  // NULL on Sample/sample-dilution rows; the SHORT run code ("Dup1"/"MS1") on
  // sourced QC rows — the legacy persisted shape (live-data verified).
  systemId:    string | null;
  jobId:       string | null;
  component:   string | null;
  strSampleId: string | null;
  sampleId:    string | null;
  sampleLogInOid: string | null;
  df:          number;
  sampleAmount: string | null;
  finalVol:    string | null;
  dupSource:   string | null;
  spikeSource: string | null;
  sourceId:    string | null;
  // Dilution provenance (parent run's code, dilution rows only) + reported-run
  // flag (legacy QCBatchSequence.OriginalSYSSamplecode / IsReport).
  originalSysSampleCode: string | null;
  isReport:    boolean;
  // Legacy-parity lookup columns carried through Sort (blank on pure QC rows).
  dilutionCount:   number;
  excludedReport:  boolean;
  units:           string | null;
  sampleName:      string | null;
  siteName:        string | null;
  samplingAddress: string | null;
  dateCreated:     string | null;
  dateReceived:    string | null;
  dateCollected:   string | null;
  client:          string | null;
  projectId:       string | null;
  projectName:     string | null;
  holdTime:        string | null;
}

// Reagents/instruments picked BEFORE the batch existed — legacy parity
// (ReagentLink/InstrumentLink are gated by user permission only, not by whether
// the batch is saved); persisted atomically with Create.
export interface QcStagedReagent {
  reagentId: string; reagentName: string; prepType: string | null;
  expiredDate: string | null; vendorLT: string | null; preparedBy: string | null;
  datePrepared: string | null; dateReceived: string | null; vendorOid: string | null;
}

export interface QcCreateBody {
  testMethodId:   string;
  templateId:     number | null;
  jobIds:         string | null;     // ';'-delimited job-id labels
  instrumentOid:  string | null;
  instrumentName: string | null;
  noRuns:         number;
  roomTemp:       string | null;
  humidity:       string | null;
  comments:       string | null;
  sequence:       QcSeqRow[];
  stagedReagents?:        QcStagedReagent[];
  stagedInstrumentOids?:  string[];
}
// One prior QC batch in the "Copy From Previous Batch" picker.
export interface QcPreviousBatch {
  abOid: string; qcBatchId: string | null; createdDate: string | null;
  jobIds: string | null; status: number; statusText: string | null; matrix: string | null;
  reagentCount: number; instrumentCount: number;
}

export interface QcCreateResult {
  qcBatchId: string; abOid: string; seqCount: number;
  samplesLinked: number;       // DISTINCT samples (2026-10-01; was the parameter-row count)
  parametersLinked?: number;   // SampleParameter rows linked
}

/** Body for the SDMS merge-data fetch (Ok → SDMS handoff): the picked template +
 *  the built sequence (expanded to sample×parameter records server-side). Header
 *  FIELD values are filled client-side from the seed. */
export interface SdmsMergeDataBody {
  templateId:   number;
  testMethodId: string;
  sequence:     QcSeqRow[];
  // The batch's staged labware — feeds the Instruments sub-sheet's mail-merge (OG
  // InstrumentDataSource). Optional; omitted → that sheet stays header-only. (Reagents
  // are built client-side from the staged reagents — see SdmsResultEntryPage.)
  instrumentOids?: string[];   // Labware.Oid values
}
export interface SdmsCalibrationMergeDataBody {
  templateId:   number;
  testMethodId: string;
  levelNo:      number;   // template's CalibrationLevelNo (sheets Level1..LevelN)
  userName?:    string;   // stamps CALIBRATEDBY (optional)
}

/** OG SetUserDropdownsFromDepartmentChain (BTLIMS :31357): one template field that should offer a
 *  ResultEntry-analyst dropdown, at its A1 `position` (`continuous` = fill DOWN the data band). */
export interface SdmsDeptChainField { fieldName: string; position: string; continuous: boolean; }
/** SDMS analyst-dropdown config: the distinct ResultEntry analysts for the batch's test method plus
 *  the fields that should offer them. Present on the merge response only when a template field is
 *  flagged `UsersFromDepartmentChain` (0 today → the client injection is a strict no-op). */
export interface SdmsDeptChainDropdown { analysts: string[]; fields: SdmsDeptChainField[]; }
/** SDMS ribbon "Report" info (GET sdms/{batch}/reports): the template's raw-data reports (OG
 *  SpreadSheetEntry_Reporting_SP) + the SpreadSheetBuilder_Header/_Detail column maps. */
export interface SdmsReportInfo {
  templateId: number; templateName: string | null; analyticalBatchId: string | null; model: number;
  reports: { reportId: number; name: string; hasXml: boolean }[];
  headerMap: { position: string; caption: string }[];
  detailMap: { columnIndex: number; columnName: string }[];
}
export interface QcRollbackResult { qcBatchId: string; oldStatus: number; affectedSamples: number }

// "Open an existing QC batch" (view/edit mode) — matches QcBatchHeaderRow
// (Application/Common/Interfaces/IQcBatchReader.cs). jobIds/instrumentOids are
// the RAW stored strings (';'-joined display labels for jobs, Oid string(s)
// for instrument) — not resolved back through the picker lists.
export interface QcBatchHeader {
  abOid: string; qcBatchId: string | null; createdDate: string | null; createdByName: string | null;
  testMethodId: string; test: string | null; matrix: string | null; method: string | null;
  jobIds: string | null; jobOids: string | null; instrumentOids: string | null; instrumentName: string | null;
  noRuns: number | null; roomTemp: string | null; humidity: string | null; comments: string | null;
  status: number; statusText: string | null; templateId: number | null; prepBatchId: string | null;
  rolledBackBy: string | null; rolledBackDate: string | null; rollbackReason: string | null;
  reviewedBy: string | null; reviewedDate: string | null; verifiedBy: string | null; verifiedDate: string | null;
  submittedByList: string | null; submittedDateMax: string | null;
  newStatusText: string | null;
}
export interface QcBatchDetail { header: QcBatchHeader; sequence: QcSeqRow[] }

// QC Batches list "New" — cascading Matrix → Test → Method picker rows
// (QcBatchPickMatrixRow / QcBatchPickTestRow in IQcBatchReader.cs).
// One (sample × analyte) result row for the SDMS "QC Batch Results" grid — a
// focused subset of the backend BatchResultRow (camelCased JSON). The endpoint
// returns ~35 fields; these are the ones the read-only results view shows.
export interface SdmsBatchResult {
  id: string | null;
  sys: string | null; parameter: string | null;
  numericResult: string | null; result: string | null; units: string | null;
  analyzedDate: string | null; analyzedBy: string | null;
  df: string | null; rptLimit: string | null; mdl: string | null; loq: string | null; uql: string | null;
  spikeAmount: string | null; sampleName: string | null; client: string | null; projectId: string | null;
  // QC Batch Results additions (2026-09-03) — all already returned by the shared reader
  // (BatchResultRow); the popup exposes them, Result Validation ignores them.
  siteName: string | null; qcTypeName: string | null;
  rpd: string | null; rec: string | null; rpdCtrlLimit: string | null; recCtrlLimit: string | null;
  qualifier: string | null; qcStatus: string | null;
  enteredBy: string | null; enteredDate: string | null;
  dupSource: string | null; dupSourceResult: string | null;
  spikeSource: string | null; spikeSourceResult: string | null;
  lastUpdatedBy: string | null; lastUpdatedDate: string | null;
  dateCollected: string | null;
  systemId: string | null; sampleId: string | null; runNo: string | null;
  volumeUsed: string | null; finalVolume: string | null;
  sigFig: string | null; cutOff: string | null; decimalPlaces: string | null;
  isReport: string | null; qcSampleResult: string | null;
  recLLimit: string | null; recULimit: string | null; rpdLLimit: string | null; rpdULimit: string | null; jobId: string | null;
  highCLimit: string | null; lowCLimit: string | null; defaultResult: string | null; sampleLocation: string | null; projectName: string | null;
  seInstrument: string | null; recCtrlLimitText: string | null; rpdCtrlLimitText: string | null; testName: string | null; methodName: string | null; matrixName: string | null;
  dilution: string | null; mcl: string | null; dld: string | null;
  calibrationIdText: string | null; calibratedBy: string | null; calibratedDate: string | null;
  internalStandardLinkText: string | null;
  seExtrasJson: string | null;
  // Row-category inputs (already on the wire — BatchResultRow); read by the SDMS
  // Enhanced rollback popup through the shared categorizeRow.
  qcRootRole?: string | null; qcRole?: string | null;
  isSurrogate?: boolean | null; isInternalStandard?: boolean | null;
}

export interface QcPickMatrix { matrixOid: string; matrixName: string | null }
export interface QcPickTest   { testMethodId: string; testName: string | null; methodNumber: string | null }

// SDMS ribbon grid lookups. OG's visible columns are Test → Test·Matrix·Method·
// TemplateName·Sx ("Sx" = count of DISTINCT pending samples for that test, NOT a
// code), and QCBatchID → JobID·Test·Matrix·Method·Template·QCBatchID(ABID)·Status
// (Status shown in View mode only). `diviion` keeps the legacy sproc's spelling.
export interface SdmsTestLookup {
  test: string | null; matrix: string | null; method: string | null;
  templateName: string | null; sx: number; testMethodId: string; templateID: number;
}
export interface SdmsQcBatchLookup {
  uqAnalyticalBatchID: string; uqCalibrationID: number; templateID: number; uqTestMethodID: string;
  jobID: string | null; test: string | null; matrix: string | null; method: string | null;
  template: string | null; abid: string | null; status: string | null; diviion: string | null;
}

interface Envelope<T> { success: boolean; data?: T; error?: { code: string; message: string } }

const unwrap = <T,>(d: Envelope<T>): T => {
  if (!d.success || d.data === undefined) throw new Error(d.error?.message ?? 'Request failed.');
  return d.data;
};

/** One SDMS row at the Engine-B compute boundary — mirrors the server DTO
 *  AlpacaIntelligence.Application.Common.Interfaces.SdmsCalcRow (camelCase). Inputs are
 *  strings (the engine reads/writes text); Computed* are filled by the compute endpoint. */
export interface SdmsCalcRow {
  rowId: string;
  numericResult?: string | null; result?: string | null;
  rawResult?: string | null;   // the engine's CalculateNumericResult source (see assembleCalcRows)
  rptLimit?: string | null; mdl?: string | null; df?: string | null; loq?: string | null; uql?: string | null;
  sysSampleCode?: string | null; spikeAmount?: string | null; dupSource?: string | null; spikeSource?: string | null;
  analyzedDate?: string | null;
  parameter?: string | null; qcType?: string | null; qcRole?: string | null; qcRootRole?: string | null;
  isSurrogate?: boolean;
  isInternalStandard?: boolean;   // 2026-09-23 engine: no RPD / %Recovery on an internal-standard row
  currentRpd?: string | null; currentRecovery?: string | null; currentQcStatus?: string | null; qualifier?: string | null;
  sigFig?: string | null; cutOff?: string | null; decimals?: string | null; defaultResult?: string | null;
  rpdSigFig?: string | null; rpdCutOff?: string | null; rpdDecimal?: string | null;
  recSigFig?: string | null; recCutOff?: string | null; recDecimal?: string | null;
  recLowerLimit?: string | null; recUpperLimit?: string | null; rpdUpperLimit?: string | null;
  ucl?: string | null;   // upper control limit — Blank QCStatus (2026-09-13 engine) fails a blank at/above it
  computedResult?: string | null; computedRecovery?: string | null; computedRpd?: string | null; computedQcStatus?: string | null;
  computedAnalyzedDate?: string | null;
  computedDupSourceResult?: string | null; computedSpikeSourceResult?: string | null;   // engine cross-row source results
}

/** One parsed instrument row from SDMS Import Files: its match keys + the dtsample FIELD → value
 *  pairs to stamp into the sheet (identifiers excluded server-side). */
export interface SdmsImportWrite {
  sysSampleCode: string;
  parameter: string;
  fields: { field: string; value: string | null }[];
  /** Dilution parsed off an Anions/ICP-MS instrument sample id (server-side band-join hint; informational here). */
  dilution?: string | null;
}
/** One picture harvested from an uploaded instrument worksheet (PFAS .xls/.xlsx: the per-sample
 *  chromatograms) - OG AddImageCollectiontoTable / BindImagestoSheet copy them onto the sample's sheet. */
export interface SdmsImportPicture {
  mime: string;            // image/png | image/jpeg | ...
  base64: string;
  row1: number; col1: number; row2: number; col2: number;   // 0-based anchor in the SOURCE sheet
  widthPx: number; heightPx: number;
  labelA: string | null;   // the text OG copies from Images!{col}{row-1} -> A{bottomRow-1}
  labelB: string | null;   // ... and Images!B{row-1} -> B{bottomRow-1}
}
export interface SdmsImportImages {
  sysSampleCode: string;   // the band row / per-sample sheet the pictures belong to
  sourceSheet: string;
  pictures: SdmsImportPicture[];
}
/** Response of `uploadInstrumentFile`: the per-row writes, the template's FIELD→A1 cell map, the
 *  parsed-row count, and any non-fatal parser/spec warnings. */
export interface SdmsImportResponse {
  writes: SdmsImportWrite[];
  fieldCells: Record<string, string>;
  rowsParsed: number;
  warnings: string[];
  /** Pictures per sample (PFAS-style per-sample-sheet uploads on a Model-1 template); absent/empty otherwise. */
  images?: SdmsImportImages[];
  /** The batch's SDMS TemplateName (drives OG's PFAS picture-placement rule). */
  templateName?: string | null;
  /** True when the server dropped the file before import, as OG drops it from its staging list: an
   *  Anions / VOC / THM (non-YAML) upload whose sample ids match no batch row (OG MatchSampleID).
   *  Nothing was written or archived; OG raises no message for it. */
  skipped?: boolean;
  /** The template's MultipleInstruments flag (VOC / THM): the page then builds OG's "Raw Data" summary. */
  multipleInstruments?: boolean;
  /** The sample this file's rows matched (OG dtRawData) — passed back when a pending-batch file is archived. */
  rawSysSampleCode?: string | null;
  /** OG's post-import zero-fill: the field (NUMERICRESULT, or USERDEFINED20 on Anions) every batch row still
   *  without a result is set to "0.000" after the import — null when OG would not (see writeImportedValuesToSheet). */
  zeroFillField?: string | null;
}

const BASE = '/api/v1/analysis/qc-batches';

/** Upload progress of an instrument-file import: bytes sent so far and in total (0 when unknown). */
export type UploadProgressFn = (sent: number, total: number) => void;
/** Axios config reporting the request body's upload progress to `onUpload` (none when not given). */
const uploadConfig = (onUpload?: UploadProgressFn) =>
  onUpload ? { onUploadProgress: (e: { loaded: number; total?: number }) => onUpload(e.loaded, e.total ?? 0) } : {};

export const qcBatchApi = {
  // QC Batches list "New" — cascading Matrix → Test → Method pickers (OG
  // parity: legacy New opens the create dashboard with NO test; each lookup is
  // gated on the previous pick). The Method pick is the definitive TestMethod.
  newPickerMatrices: () =>
    api.get<Envelope<QcPickMatrix[]>>(`${BASE}/new-picker/matrices`).then(r => unwrap(r.data)),
  newPickerTests: (matrixOid: string) =>
    api.get<Envelope<QcPickTest[]>>(`${BASE}/new-picker/tests`, { params: { matrixOid } }).then(r => unwrap(r.data)),
  /** The batch's SDMS workbook (.xlsx bytes) — its SAVED sheet when present, else
   *  the empty template for its TemplateID, else null on 204 ("nothing on file").
   *  Reuses the endpoint the Result-Validation preview already uses, so Result
   *  Entry and the preview read the same blob. */
  sdmsWorkbook: async (batchOid: string) => {
    const res = await api.get(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/sdms-workbook`,
      { responseType: 'arraybuffer' });
    const buf = res.data as ArrayBuffer;
    if (res.status === 204 || !buf || buf.byteLength === 0) return null;
    return {
      buf,
      source:       String(res.headers?.['x-sdms-source'] ?? ''),
      templateName: String(res.headers?.['x-sdms-template-name'] ?? ''),
    };
  },

  /** SDMS Result ENTRY — persist the analyst's typed results by saving the edited
   *  workbook blob against the batch (legacy SpreadsheetEntryController's
   *  `ABID.SpreadSheet = …SaveDocument(OpenXml)`).  Multipart: a `workbook` part
   *  (the .xlsx bytes) plus an optional `changes` part — the per-cell edits the sheet
   *  diffed (SysSampleCode|Parameter|Field, old→new, A1 ref) — so the backend can AUDIT
   *  each changed cell against the batch ("QC Batches" / batch Oid).  Entry still does
   *  NOT write SampleInfo and does NOT run validation's self-review gate (blob-only save
   *  + audit).  On an Update the reason is armed as X-Audit-Reason by the caller's save
   *  flow → recorded on every audit row.  The saved blob is what `sdmsWorkbook` serves
   *  back on reopen, so the typed values survive. */
  saveSdmsEntryWorkbook: (batchOid: string, bytes: ArrayBuffer, changes?: readonly SdmsCellChange[],
                          calibrationOid?: string | null, importValues?: readonly SdmsImportValue[],
                          instrumentImported?: boolean) => {
    const form = new FormData();
    form.append('workbook',
      new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      'sdms.xlsx');
    if (changes && changes.length)
      form.append('changes', JSON.stringify(changes.map(c => ({
        sysSampleCode: c.label ?? '', parameter: c.parameter ?? '', field: c.field ?? '',
        old: c.old ?? '', new: c.new ?? '', ref: c.ref ?? '', ...(c.derived ? { derived: true } : {}),
      }))));
    // Attach Calibration: link this calibration to the batch (OG ABID.Calibration = calib).
    if (calibrationOid) form.append('calibrationOid', calibrationOid);
    // Values an instrument import gave a row that no sheet cell shows (OG saves them too — defect 7).
    if (importValues && importValues.length) form.append('importValues', JSON.stringify(importValues));
    // An instrument import ran since the sheet was loaded: OG then holds each row's own Testparameter SigFig /
    // CutOff (PreloadRoundingSettings), so a first save does not copy the rowset's.
    if (instrumentImported) form.append('instrumentImported', 'true');
    // No Content-Type — the browser sets the multipart boundary for FormData.
    return api.put<Envelope<{ bytes: number; cellChanges: number; calibrationId?: string | null }>>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/workbook`,
      form,
    ).then(r => unwrap(r.data));
  },

  /** SDMS Result Entry "Submit" (legacy Complete). Save the workbook FIRST, then call
   *  this. `qcFailConfirm` true = the batch has a failing QC row with no qualifier; ask the
   *  analyst and re-call with force=true. A hard validation block (Analyzed-By empty /
   *  duplicate report flag) rejects with 400 and is thrown by unwrap. */
  /** 'Data Package' action: validate + find-or-create the batch's DataPackage record (and seed
   *  its Check Points). `listMode` = the QC-batch-LIST hyperlink (OG :13905 — duplicate gate only);
   *  omit/false = the SDMS-entry :8910 button (all gates). Returns { oid, status } where status is
   *  the DataPackage.Status (1 PendingSubmission → Submit; 2 PendingValidation → Validate). Throws
   *  (400) with the gate message on failure. */
  createSdmsDataPackage: (batchOid: string, listMode = false) =>
    api.post<Envelope<{ oid: string; status: number | null; qcFailCount: number;
      packageCreatedDate: string | null; batchCreatedBy: string | null }>>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/data-package${listMode ? '?listMode=true' : ''}`, null)
      .then(r => unwrap(r.data)),

  /** OG-parity (SpreadsheetEntryController :2283-2293): whether the SDMS "Data Package" ribbon
   *  button is enabled globally (ResultEntryDefaultSettings.DataPackageValue = Yes). */
  sdmsDataPackageEnabled: () =>
    api.get<Envelope<{ enabled: boolean }>>(
      `/api/v1/analysis/sdms/data-package-enabled`, { silentLoading: true } as never)
      .then(r => unwrap(r.data).enabled),

  /** OG :3636-3645 — ResultEntryDefaultSettings.ReagentEntrySDMS: false locks the Reagents sheet. */
  sdmsReagentEntryEnabled: () =>
    api.get<Envelope<{ enabled: boolean }>>(
      `/api/v1/analysis/sdms/reagent-entry-enabled`, { silentLoading: true } as never)
      .then(r => unwrap(r.data).enabled),

  /** DataPackage Submit (OG QBIDSubmit :3172 / AddSubmit :2452). In SDMS the analyst only SUBMITS the
   *  Data Package: 'submit' persists the posted Check Point edits, runs ValidateCheckPoint, then advances
   *  the package 1→2 AND advances the batch to Pending Validation (OG's Complete/QBIDSubmit). The QC-fail
   *  case returns { qcFailConfirm:true, qcFailCount } (nothing written) so the popup asks and re-calls with
   *  force=true. Throws (400) with the gate/validation message on a hard failure. */
  dataPackageTransition: (
    batchOid: string,
    action: 'submit',
    checkpoints?: Array<{ oid: string; yes: boolean; no: boolean; na: boolean; comment: string | null; qualifier: string | null }>,
    force = false,
    /** A PLM package: the PLM Results rows ticked (G5 Self Submit). The answer's
     *  `advanced` is false while other analysts' layers are still unsubmitted. */
    plmSequenceOids?: string[],
  ) =>
    api.post<Envelope<{ ok?: boolean; qcFailConfirm?: boolean; qcFailCount?: number; advanced?: boolean }>>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/data-package/transition`,
      { action, checkpoints: checkpoints ?? [], force, ...(plmSequenceOids ? { plmSequenceOids } : {}) })
      .then(r => unwrap(r.data)),

  /** Add one attachment to the batch's Data Package (OG DataPackageAttachments "New").
   *  Multipart POST — the browser sets the multipart boundary from the FormData, so we
   *  do NOT set Content-Type. Editable only while the package is PendingSubmission; the
   *  server 400s otherwise. Returns the new attachment + FileData oids. */
  uploadDataPackageAttachment: (batchOid: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<Envelope<{ oid: string; fileOid: string; name: string }>>(
        `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/attachments`, form)
      .then(r => unwrap(r.data));
  },

  /** Remove one Data Package attachment (OG nested-grid Delete) — soft-delete the link
   *  row (no FileData cascade). 400 if the package is past PendingSubmission. */
  deleteDataPackageAttachment: (attachmentOid: string) =>
    api
      .delete<Envelope<{ name: string | null }>>(
        `/api/v1/analysis/result-validation/attachments/${encodeURIComponent(attachmentOid)}`)
      .then(r => unwrap(r.data)),

  /** Rename / re-categorise one Data Package attachment — OG's nested-grid Edit
   *  (DataPackageAttachments_DetailView). Only Name + Category are writable: the
   *  blob is replaced by an upload, and Date/Operator are provenance stamps. */
  updateDataPackageAttachment: (
    attachmentOid: string, name: string, categoryOid: string | null, operatorOid: string | null,
  ) =>
    api
      .put<Envelope<{ name: string | null }>>(
        `/api/v1/analysis/result-validation/attachments/${encodeURIComponent(attachmentOid)}`,
        // FK Oids, not display text: DataPackageAttachments.Category -> dbo.Category
        // and .Operator -> dbo.Employee are both uniqueidentifier columns.
        { name, categoryOid, operatorOid })
      .then(r => unwrap(r.data)),

  /** The Category + Operator lookups behind the attachment dialog — legacy
   *  renders both as dropdowns (dbo.Category / dbo.Employee). */
  dataPackageAttachmentOptions: () =>
    api
      .get<Envelope<{ categories: { id: string; name: string | null }[];
                      operators:  { id: string; name: string | null }[] }>>(
        `/api/v1/analysis/result-validation/attachment-options`)
      .then(r => unwrap(r.data)),

  submitSdms: (batchOid: string, force: boolean) =>
    api.post<Envelope<{ qcFailConfirm: boolean; qcFailCount?: number; rowsComplete?: number; skipped?: number }>>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/submit`, null, { params: { force } },
    ).then(r => unwrap(r.data)),

  /** SDMS "Import Files": upload an instrument raw-data file for the batch. The server parses it
   *  with the test's DataTransfer YAML spec (rehosted AlpacaLims.InstrumentParser) and returns the
   *  per-row field values to stamp into the sheet plus the Field→A1 cell map; the client writes them
   *  into the matching band rows. Multipart POST (the browser sets the boundary — no Content-Type).
   *  400 with the gate message when the test has no DataTransfer template / no YAML spec, or the
   *  file is unreadable. */
  uploadInstrumentFile: (batchOid: string, file: File, dataTransferName?: string | null) => {
    const form = new FormData();
    form.append('file', file);
    if (dataTransferName) form.append('dataTransferName', dataTransferName);   // the dialog's "Name" pick
    return api
      .post<Envelope<SdmsImportResponse>>(
        `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/import`, form)
      .then(r => unwrap(r.data));
  },

  /** SDMS "Import Files" into an UNSAVED batch (OG imports before the first Save): the file is parsed
   *  against the same merge inputs the unsaved sheet was built from (the sdmsMergeData body). Nothing
   *  is archived server-side — call archiveInstrumentRawFile once the first Save creates the batch. */
  uploadInstrumentFilePending: (body: SdmsMergeDataBody, file: File, dataTransferName?: string | null) => {
    const form = new FormData();
    form.append('file', file);
    if (dataTransferName) form.append('dataTransferName', dataTransferName);
    form.append('payload', JSON.stringify(body));
    return api
      .post<Envelope<SdmsImportResponse>>('/api/v1/analysis/sdms/import-pending', form)
      .then(r => unwrap(r.data));
  },

  /** SDMS "Import Files", every staged file in ONE request (2026-10-01): the server reads the batch's merge data
   *  once (the per-file calls re-read it for each file) and parses the files in this order, each getting its own
   *  result; `error` is set where the single-file call would have answered 400 (gate message / too large / empty)
   *  and the other files still import. Each file is archived exactly as uploadInstrumentFile archives it. */
  uploadInstrumentFiles: (batchOid: string, files: readonly File[], dataTransferName?: string | null,
    onUpload?: UploadProgressFn) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    if (dataTransferName) form.append('dataTransferName', dataTransferName);
    return api
      .post<Envelope<{ results: Array<SdmsImportResponse & { error?: string | null }> }>>(
        `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/import`, form, uploadConfig(onUpload))
      .then(r => unwrap(r.data)?.results ?? []);
  },

  /** uploadInstrumentFiles for an UNSAVED batch (the uploadInstrumentFilePending body) - one request, one merge read. */
  uploadInstrumentFilesPending: (body: SdmsMergeDataBody, files: readonly File[], dataTransferName?: string | null,
    onUpload?: UploadProgressFn) => {
    const form = new FormData();
    for (const f of files) form.append('files', f);
    if (dataTransferName) form.append('dataTransferName', dataTransferName);
    form.append('payload', JSON.stringify(body));
    return api
      .post<Envelope<{ results: Array<SdmsImportResponse & { error?: string | null }> }>>('/api/v1/analysis/sdms/import-pending', form,
        uploadConfig(onUpload))
      .then(r => unwrap(r.data)?.results ?? []);
  },

  /** The template's DataTransfer names for the Import Instrument File dialog's "Name" dropdown (OG
   *  DataTransfer_Sp — first one selected). */
  sdmsDataTransferNames: (templateId: number) =>
    api.get<Envelope<string[]>>('/api/v1/analysis/sdms/data-transfers', { params: { templateId } })
      .then(r => unwrap(r.data) ?? []),

  /** Archive a file imported before the batch existed (see uploadInstrumentFilePending) against the
   *  batch the first Save just created — OG's Save-time RawDataAttachment write, with the sample the import
   *  matched the file to (MultipleInstruments templates, OG :7491). Returns a warning or null. */
  archiveInstrumentRawFile: (batchOid: string, file: File, sysSampleCode?: string | null) => {
    const form = new FormData();
    form.append('file', file);
    if (sysSampleCode) form.append('sysSampleCode', sysSampleCode);
    return api
      .post<Envelope<{ warning: string | null }>>(
        `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/raw-file`, form)
      .then(r => unwrap(r.data)?.warning ?? null);
  },

  /** SDMS "Import Files" on a CALIBRATION set (New Calibration, Model=2 template — the PFASCalib
   *  DataTransfer row). The server parses the instrument calibration workbook (OG
   *  InstrumentImportCalibFromExcel) and returns per-(level × parameter) writes keyed
   *  SysSampleCode = "Level{n}" — the per-level sheet names — plus the template's FIELD→A1 map, so the
   *  shared sheet write-back places them. 400 with the gate message on failure. */
  uploadCalibrationFile: (templateId: number, testMethodId: string, file: File, dataTransferName?: string | null,
    onUpload?: UploadProgressFn) => {
    const form = new FormData();
    form.append('file', file);
    if (dataTransferName) form.append('dataTransferName', dataTransferName);
    return api
      .post<Envelope<SdmsImportResponse>>(
        '/api/v1/analysis/sdms/calibration/import', form, { params: { templateId, testMethodId }, ...uploadConfig(onUpload) })
      .then(r => unwrap(r.data));
  },

  /** SDMS merge INPUTS for the Ok → SDMS handoff (batch not yet saved): the per-test
   *  template (bytes) + the sequence expanded to sample×parameter records. The merge
   *  itself runs client-side (ExcelJS, so the result stays Univer-readable). Null on
   *  204 (template has no blob). NB: SDMS endpoints live on AnalysisController
   *  (/api/v1/analysis/…), NOT under BASE (/api/v1/analysis/qc-batches). */
  sdmsMergeData: async (body: SdmsMergeDataBody) => {
    const res = await api.post<Envelope<{ templateBase64: string; templateName: string | null; traceability?: string | null; model?: number;
      records: Record<string, unknown>[]; instruments?: Record<string, unknown>[];
      fieldCells?: Record<string, string> | null; templateId?: number; deptChainDropdown?: SdmsDeptChainDropdown | null; entryFields?: string[] | null;
      continuousHeaderFields?: string[] | null; calibrationCells?: Record<string, string> | null }>>(
      '/api/v1/analysis/sdms/merge-data', body);
    if (res.status === 204) return null;
    const data = unwrap(res.data);
    if (!data?.templateBase64) return null;
    const bin = atob(data.templateBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return {
      template: bytes.buffer, templateName: data.templateName ?? '', traceability: data.traceability ?? null,
      model: data.model ?? 0, records: data.records ?? [], instruments: data.instruments ?? [],
      fieldCells: data.fieldCells ?? null, templateId: data.templateId ?? 0,
      deptChainDropdown: data.deptChainDropdown ?? null,
      entryFields: data.entryFields ?? null,   // config-derived analyst-entry fields (drift-warning suppression on a fresh merge)
      // Continuous fields placed in a header cell (VOC Remark B6) — the merge leaves those cells to the batch header.
      continuousHeaderFields: data.continuousHeaderFields ?? null,
      // OG CalibAndABIDSupport: CalibrationTable fields written into the Calibration sheet (CALIBRATIONID G2 …).
      calibrationCells: data.calibrationCells ?? null,
    };
  },

  /** RE-OPEN a SAVED batch by re-merging from the tables (AI divergence from OG's blob
   *  reload): the batch's template (bytes) + records rebuilt from SpreadSheetEntry_SampleInfo
   *  WITH entered values (AI_Analysis_Sdms_OpenMergeData_SP). Same shape as sdmsMergeData;
   *  the client mail-merges it exactly like a new batch. Null on 204 (batch/template gone →
   *  caller falls back to the stored blob). */
  /** A SpreadSheetBuilder template by its exact name as OOXML bytes — the "Raw Data Summary"
   *  template OG mail-merges into a multi-instrument batch's "Raw Data" sheet (SpreadsheetEntryController
   *  :31482). Null on 204 (no active template of that name). */
  sdmsTemplateByName: async (name: string): Promise<ArrayBuffer | null> => {
    const res = await api.get<ArrayBuffer>(`/api/v1/analysis/sdms/template-by-name?name=${encodeURIComponent(name)}`,
      { responseType: 'arraybuffer' });
    if (res.status === 204 || !res.data || (res.data as ArrayBuffer).byteLength === 0) return null;
    return res.data;
  },

  sdmsOpenMergeData: async (batchOid: string) => {
    const res = await api.get<Envelope<{ templateBase64: string; templateName: string | null; traceability?: string | null; model?: number;
      records: Record<string, unknown>[]; instruments?: Record<string, unknown>[];
      fieldCells?: Record<string, string> | null; templateId?: number; deptChainDropdown?: SdmsDeptChainDropdown | null;
      calibrationOid?: string | null; calibrationId?: string | null; entryFields?: string[] | null;
      continuousHeaderFields?: string[] | null; calibrationCells?: Record<string, string> | null }>>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/open-merge-data`);
    if (res.status === 204) return null;
    const data = unwrap(res.data);
    if (!data?.templateBase64) return null;
    const bin = atob(data.templateBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return {
      template: bytes.buffer, templateName: data.templateName ?? '', traceability: data.traceability ?? null,
      model: data.model ?? 0, records: data.records ?? [], instruments: data.instruments ?? [],
      fieldCells: data.fieldCells ?? null, templateId: data.templateId ?? 0,
      deptChainDropdown: data.deptChainDropdown ?? null,
      calibrationOid: data.calibrationOid ?? null, calibrationId: data.calibrationId ?? null,
      // Write=1 (analyst-entered) field names — the reopen merge writes these as NUMBERS so the
      // template number format shows the decimals the analyst saw (OG blob parity).
      entryFields: data.entryFields ?? null,
      // Continuous fields placed in a header cell (VOC Remark B6): their stored value is the band column's, so
      // the merge leaves those header cells to the batch header.
      continuousHeaderFields: data.continuousHeaderFields ?? null,
      // OG CalibAndABIDSupport: CalibrationTable fields written into the Calibration sheet (CALIBRATIONID G2 …).
      calibrationCells: data.calibrationCells ?? null,
    };
  },

  /** Model=2 (CalibrationSet) merge feed — template blob + one record per
   *  parameter x level (AI_Analysis_Sdms_CalibrationMergeData_SP). The client
   *  clones the main sheet into Level1..LevelN (sdmsMailMerge.ts, model===2).
   *  Used by the (future) New-Calibration entry flow. */
  sdmsCalibrationMergeData: async (body: SdmsCalibrationMergeDataBody) => {
    const res = await api.post<Envelope<{ templateBase64: string; templateName: string | null; traceability?: string | null; model?: number;
      records: Record<string, unknown>[]; instruments?: Record<string, unknown>[];
      fieldCells?: Record<string, string> | null; templateId?: number }>>(
      '/api/v1/analysis/sdms/calibration-merge-data', body);
    if (res.status === 204) return null;
    const data = unwrap(res.data);
    if (!data?.templateBase64) return null;
    const bin = atob(data.templateBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return {
      template: bytes.buffer, templateName: data.templateName ?? '', traceability: data.traceability ?? null,
      model: data.model ?? 2, records: data.records ?? [], instruments: data.instruments ?? [],
      fieldCells: data.fieldCells ?? null, templateId: data.templateId ?? 0,
    };
  },

  /** Persist a new SDMS calibration (New-Calibration Save). Rows = per-(parameter x
   *  level) values read back from the Level sheets; workbookBase64 = the .xlsx blob. */
  sdmsCalibrationSave: async (body: {
    templateId: number; testMethodId: string; methodOid?: string | null;
    matrixName?: string | null; instrument?: string | null; workbookBase64?: string | null;
    rows: Array<{ levelNo: number; uqTestParameterId?: string | null; parameterId?: string | null;
                  reading?: number | null; conc?: number | null; slope?: number | null;
                  intercept?: number | null; rcap2?: number | null; absorbance?: number | null }>;
  }) => {
    const res = await api.post<Envelope<{ calibrationId: string; oid: string; rows: number }>>(
      '/api/v1/analysis/sdms/calibration/save', body);
    return unwrap(res.data);
  },

  /** Saved SDMS calibrations for a test method (open-existing picker). */
  sdmsCalibrations: async (testMethodId: string) => {
    const res = await api.get<Envelope<Array<{ oid: string; calibrationId: string | null;
      createdDate: string | null; createdByName: string | null; status: number | null }>>>(
      '/api/v1/analysis/sdms/calibrations', { params: { testMethodId } });
    return unwrap(res.data) ?? [];
  },

  /** Per-parameter %Recovery + RPD rounding settings for SDMS Apply Rounding (full parity). */
  sdmsRoundingSettings: async (testMethodId: string, templateId: number) => {
    const res = await api.get<Envelope<{ fieldCells: Record<string, string> | null; settings: Array<{
      parameterName: string | null;
      qcTypeName: string | null; isDefaultComponent: boolean; isIsOrSurrogate: boolean;   // OG ResolveTestparameter keys
      sigFig: string | null; cutOff: string | null; decimalPlaces: string | null;
      rptLimit: string | null; defaultResult: string | null;
      recSigFig: string | null; recCutOff: string | null; recDecimal: string | null;
      rpdSigFig: string | null; rpdCutOff: string | null; rpdDecimal: string | null }>; applyOnSave?: boolean }>>(
      '/api/v1/analysis/sdms/rounding-settings', { params: { testMethodId, templateId } });
    const d = unwrap(res.data);
    // applyOnSave: OG's Save auto-applies rounding (ResultEntryDefaultSettings.ApplyRoundingOnsave = On).
    return { fieldCells: d?.fieldCells ?? null, settings: d?.settings ?? [], applyOnSave: d?.applyOnSave === true };
  },

  /** Engine B — does this SDMS template have runnable (Fixed) Formula-Library bindings?
   *  The client gates on this: only bound templates are driven by the server compute;
   *  every other template keeps its existing Univer formula path untouched.
   *  NOTE the envelope shape differs — `hasFormulas` is a TOP-LEVEL field, not `data`. */
  sdmsHasFormulas: async (templateId: number): Promise<boolean> => {
    if (!Number.isFinite(templateId) || templateId <= 0) return false;
    const res = await api.get<{ success: boolean; hasFormulas?: boolean }>(
      `/api/v1/analysis/sdms/has-formulas/${templateId}`);
    return res.data?.hasFormulas === true;
  },

  /** Engine B — the fields this template's runnable formulas write (their TargetFields, e.g.
   *  "Result", "%Recovery", "RPD"). [] for a template Engine B does not drive, or on any failure. */
  sdmsEngineTargets: async (templateId: number): Promise<string[]> => {
    if (!Number.isFinite(templateId) || templateId <= 0) return [];
    const res = await api.get<{ success: boolean; targetFields?: string[] }>(
      `/api/v1/analysis/sdms/engine-targets/${templateId}`, { silentLoading: true } as never);
    return res.data?.targetFields ?? [];
  },

  /** Engine B — compute %Recovery / RPD / QCStatus / Result for a whole batch's rows in
   *  one pass (rows are computed together so cross-row Recovery/RPD can resolve their
   *  Dup/Spike source siblings). Read-only; nothing is persisted. */
  sdmsCompute: (templateId: number, rows: SdmsCalcRow[]) =>
    api.post<Envelope<SdmsCalcRow[]>>('/api/v1/analysis/sdms/compute',
      { templateId, rows }, { silentLoading: true } as never).then(r => unwrap(r.data)),

  /** Engine B — recompute one edited sample group: the edited sample's rows plus its
   *  Dup/MS/MSD dependents. Send the whole batch's rows (cross-row resolution needs the
   *  source siblings); only the affected rows come back. */
  sdmsRecomputeSampleGroup: (templateId: number, rows: SdmsCalcRow[], editedSysSampleCode: string) =>
    api.post<Envelope<SdmsCalcRow[]>>('/api/v1/analysis/sdms/recompute-sample-group',
      { templateId, rows, editedSysSampleCode }, { silentLoading: true } as never).then(r => unwrap(r.data)),

  /** Open an existing calibration read-only: template blob + fieldCells + the SAVED rows
   *  (OG regenerates the sheet from CalibrationInfo — it stores no blob). Null when absent. */
  // SDMS ribbon "Report" (OG GenerateReports): the batch's raw-data report list + the
  // header/detail column maps the client needs to extract the live sheet's band.
  sdmsReportInfo: async (batchOid: string) => {
    const res = await api.get<Envelope<SdmsReportInfo>>(`/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/reports`);
    return unwrap(res.data);
  },
  // Render one report from the posted live-sheet data → PDF in a new tab (OG ReportPreview
  // ExportToPdf + window.open). Same open pattern as openReagentCoa.
  openSdmsReport: async (batchOid: string, reportId: number, data: { rows: (string | null)[][]; headerCells: Record<string, string | null> }) => {
    const res = await api.post<Blob>(
      `/api/v1/analysis/sdms/${encodeURIComponent(batchOid)}/reports/${reportId}/pdf`, data, { responseType: 'blob' });
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  sdmsCalibrationOpen: async (oid: string) => {
    const res = await api.get<Envelope<{ templateBase64: string; templateName: string | null;
      traceability?: string | null; model?: number; records: Record<string, unknown>[];
      fieldCells?: Record<string, string> | null; templateId?: number }>>(
      `/api/v1/analysis/sdms/calibration/${encodeURIComponent(oid)}/open`);
    if (res.status === 204) return null;
    const data = unwrap(res.data);
    if (!data?.templateBase64) return null;
    const bin = atob(data.templateBase64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return {
      template: bytes.buffer, templateName: data.templateName ?? '', traceability: data.traceability ?? null,
      model: data.model ?? 2, records: data.records ?? [], fieldCells: data.fieldCells ?? null, templateId: data.templateId ?? 0,
    };
  },

  /** Read-only (sample × analyte) results for a batch — the SDMS "QC Batch
   *  Results" grid. Reuses the endpoint the Result-Validation package view reads
   *  (dbo.SpreadSheetEntry_SampleInfo ⋈ SampleParameter/SampleLogIn). */
  sdmsBatchResults: (batchOid: string, opts?: { silent?: boolean }) =>
    api.get<Envelope<SdmsBatchResult[]>>(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/batch-results`,
      // QC Batch Results wants the analyte's RL/MDL/LOQ/UQL filled on QC rows too. This flag is
      // SDMS-only; Result Validation calls the same endpoint WITHOUT it, so its grid is unchanged.
      // `silent`: the background warm-up the SDMS page fires once its sheet is ready — no progress bar.
      { params: { fillLimits: true }, ...(opts?.silent ? { silentLoading: true } : {}) } as never,
    ).then(r => unwrap(r.data)),

  /** SDMS QC Batch Results COLUMN set for a batch — the per-template field list
   *  (fieldName = storage/merge key, caption = header) ordered by OG Sort. Empty
   *  when the batch has no template (client falls back to the static columns). */
  sdmsGridColumns: (batchOid: string) =>
    api.get<Envelope<{ fieldName: string; caption: string; sort: number }[]>>(
      `/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/sdms-grid-columns`,
    ).then(r => unwrap(r.data)),

  // SDMS ribbon grid lookups (mode = the ribbon's Mode combo: 'Enter' | 'View').
  sdmsTests: (mode: string) =>
    api.get<Envelope<SdmsTestLookup[]>>(`${BASE}/sdms/tests`, { params: { mode } }).then(r => unwrap(r.data)),
  /** `testMethodId` is OPTIONAL: omitting it is OG's View-mode call (`EXEC … @Mode`
   *  with no test), which lists every test's batches. Enter mode does need one —
   *  its legacy branch has no null-guard on the test, so it returns nothing without.
   *  `months` is the ribbon Date Filter (keep batches from the last N months; omit = All). */
  sdmsQcBatches: (testMethodId: string | undefined, mode: string, months?: number) =>
    api.get<Envelope<SdmsQcBatchLookup[]>>(`${BASE}/sdms/qc-batches`,
      { params: { testMethodId: testMethodId || undefined, mode, months: months ?? undefined } }).then(r => unwrap(r.data)),

  newPickerMethods: (testMethodId: string) =>
    api.get<Envelope<QcPickTest[]>>(`${BASE}/new-picker/methods`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  jobs: (testMethodId: string) =>
    api.get<Envelope<QcJob[]>>(`${BASE}/jobs`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  instruments: (testMethodId: string) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/instruments`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  templates: (testMethodId: string) =>
    api.get<Envelope<QcTemplate[]>>(`${BASE}/templates`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  prepBatches: (testMethodId: string) =>
    api.get<Envelope<QcPrepBatch[]>>(`${BASE}/prep-batches-for-test`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  qcTypes: (testMethodId: string) =>
    api.get<Envelope<QcTypeOption[]>>(`${BASE}/qc-types`, { params: { testMethodId } }).then(r => unwrap(r.data)),

  load: (testMethodId: string, jobOids: string[]) =>
    api.post<Envelope<QcSample[]>>(`${BASE}/load`, { testMethodId, jobOids }).then(r => unwrap(r.data)),

  // Import a Sample Prep Batch's prepared samples (same shape as `load`) — legacy
  // "select a Prep Batch" path; the rows then go through Sort + Create unchanged.
  loadFromPrepBatch: (testMethodId: string, prepBatchOid: string) =>
    api.post<Envelope<QcSample[]>>(`${BASE}/load-from-prep-batch`, { testMethodId, prepBatchOid }).then(r => unwrap(r.data)),

  // OG-exact prep import: the prep batch's already-built sequence (samples + QC
  // interleave) as QcSeqRow[], so the grid opens SORTED (verbatim copy).
  seqFromPrepBatch: (prepBatchOid: string) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/seq-from-prep-batch`, { prepBatchOid }).then(r => unwrap(r.data)),

  buildSequence: (testMethodId: string, templateId: number | null, noRuns: number, samples: QcSample[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/build-sequence`,
      { testMethodId, templateId, noRuns, samples }).then(r => unwrap(r.data)),

  create: (body: QcCreateBody) =>
    api.post<Envelope<QcCreateResult>>(`${BASE}/create`, body).then(r => unwrap(r.data)),

  // Revert a saved batch to Pending Result Entry (legacy QCBatchRollback parity).
  rollback: (abOid: string, reason: string) =>
    api.post<Envelope<QcRollbackResult>>(`${BASE}/${abOid}/rollback`, { reason }).then(r => unwrap(r.data)),

  // "Open an existing QC batch" (view/edit mode) — header + built sequence.
  getDetail: (abOid: string) =>
    api.get<Envelope<QcBatchDetail>>(`${BASE}/${abOid}/detail`).then(r => unwrap(r.data)),

  // Persist edits to a saved batch (A1 — reconcile-on-Save). Posts the FULL
  // current grid (rows with seqOid = existing, without = added; any stored row
  // whose Oid is absent was removed) + Room Temp/Humidity.
  updateBatch: (abOid: string, body: { roomTemp: string | null; humidity: string | null; comments: string | null; sequence: QcSeqRow[] }) =>
    api.put<Envelope<{ added: number; removed: number; updated: number }>>(`${BASE}/${abOid}`, body)
      .then(r => unwrap(r.data)),

  // Delete a saved QC batch (legacy DeleteAction parity — cascade + return samples to
  // the queue). Guard errors (non-deletable status / reported / results entered) come
  // back as a rejected promise with the friendly message.
  deleteBatch: (abOid: string) =>
    api.delete<Envelope<{ qcBatchId: string }>>(`${BASE}/${abOid}`).then(r => unwrap(r.data)),

  // Instant per-row ExcludedReport toggle on a saved batch (legacy :5595 — the one
  // true per-action write; persists SampleParameter.NotReport immediately so it is
  // not lost if the user navigates away without a full Save).
  setExcludedReport: (abOid: string, seqOid: string, excluded: boolean) =>
    api.put<Envelope<{ affected: number }>>(
      `${BASE}/${abOid}/sequence/${seqOid}/excluded-report`, { excluded },
      { silentLoading: true } as never).then(r => unwrap(r.data)),

  // Prior QC batches for the "Copy From Previous Batch" picker (same test method,
  // excluding the current one).
  previousBatches: (testMethodId: string, excludeAbOid?: string) =>
    api.get<Envelope<QcPreviousBatch[]>>(`${BASE}/previous`,
      { params: { testMethodId, excludeAbOid } }).then(r => unwrap(r.data)),

  // Reagents (Others tab) — legacy ReagentLink_Execute parity.
  linkedReagents: (abOid: string) =>
    api.get<Envelope<QcLinkedReagent[]>>(`${BASE}/${abOid}/reagents`).then(r => unwrap(r.data)),

  /** "Add Reagents" picker catalog. `testMethodId` scopes it to the batch's test —
   *  without it the picker listed every reagent in the lab (VOC/TOC preparations on
   *  an Alkalinity batch; user report 2026-08-07). */
  /** `months` = the picker's Date Filter (1Y = 12), applied by the server so only those rows
   *  are sent (2026-10-01); omit for "All". */
  availableReagents: (testMethodId?: string | null, months?: number | null) =>
    api.get<Envelope<QcAvailableReagent[]>>(`${BASE}/reagents/available`, {
      params: { ...(testMethodId ? { testMethodId } : {}), ...(months ? { months } : {}) },
    }).then(r => unwrap(r.data)),

  linkReagent: (abOid: string, reagent: QcAvailableReagent) =>
    api.post<Envelope<{ reagentOid: string }>>(`${BASE}/${abOid}/reagents`, {
      reagentId: reagent.reagentID, reagentName: reagent.reagentName, prepType: reagent.prepType,
      expiredDate: reagent.expiryDate, vendorLT: reagent.vendorLT, preparedBy: reagent.preparedBy,
      datePrepared: reagent.datePrepared, dateReceived: reagent.dateReceived, vendorOid: reagent.vendor ?? null,
    }).then(r => unwrap(r.data)),

  unlinkReagent: (abOid: string, reagentOid: string, reagentId: string | null) =>
    api.delete<Envelope<{ unlinked: boolean }>>(`${BASE}/${abOid}/reagents/${reagentOid}`, { params: { reagentId } }).then(r => unwrap(r.data)),

  // COA (Certificate of Analysis) — "LT"-prefixed reagent lots only (legacy
  // PreviewCOA_Execute; "LLT" dynamic-report generation is out of scope). OPENS
  // the certificate in a NEW BROWSER TAB (legacy ShowPdfInNewTab parity; was a
  // forced download before). Fetched as an authenticated blob — a bare <a>/window
  // .open on the URL can't carry the JWT this endpoint requires — then the blob
  // URL is opened in a new tab, where the browser renders the inline PDF.
  openReagentCoa: async (reagentId: string) => {
    const res = await api.get<Blob>(`${BASE}/reagents/${encodeURIComponent(reagentId)}/coa`, { responseType: 'blob' });
    const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    // Keep the object URL alive long enough for the new tab to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },

  // COA availability per ReagentID — the Others tab of an UNSAVED batch uses it so a
  // staged reagent shows its COA link before save (user report 2026-09-29).
  reagentCoaFlags: (reagentIds: string[]) =>
    api.post<Envelope<{ reagentID: string; hasCoa: boolean }[]>>(`${BASE}/reagents/coa-flags`, { reagentIds })
      .then(r => unwrap(r.data)),

  // Instruments (Others tab) — legacy InstrumentLink_Execute parity. A DIFFERENT
  // catalog from `instruments()` above (that one is test-method-scoped, for the
  // single header field; this is ALL active instruments, legacy parity).
  linkedInstruments: (abOid: string) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/${abOid}/instruments`).then(r => unwrap(r.data)),

  /** Add-Instrument picker catalog. `testMethodId` scopes it to the instruments
   *  linked to that test on the legacy "Instruments, Lab Analysis" screen. */
  availableInstruments: (testMethodId?: string | null) =>
    api.get<Envelope<QcInstrument[]>>(`${BASE}/instruments/available`,
      testMethodId ? { params: { testMethodId } } : undefined).then(r => unwrap(r.data)),

  linkInstrument: (abOid: string, instrumentOid: string, instrumentName: string | null) =>
    api.post<Envelope<{ linked: boolean }>>(`${BASE}/${abOid}/instruments/${instrumentOid}`, null,
      { params: { instrumentName } }).then(r => unwrap(r.data)),

  unlinkInstrument: (abOid: string, instrumentOid: string, instrumentName: string | null) =>
    api.delete<Envelope<{ unlinked: boolean }>>(`${BASE}/${abOid}/instruments/${instrumentOid}`,
      { params: { instrumentName } }).then(r => unwrap(r.data)),

  // Add Samples to a saved batch — legacy AddQCBatchSample_* parity.
  addSampleCandidates: (abOid: string) =>
    api.get<Envelope<QcSample[]>>(`${BASE}/${abOid}/add-sample-candidates`).then(r => unwrap(r.data)),

  addSamples: (abOid: string, sampleLogInOids: string[]) =>
    api.post<Envelope<{ seqAdded: number; samplesLinked: number }>>(`${BASE}/${abOid}/add-samples`,
      { sampleLogInOids }).then(r => unwrap(r.data)),

  // Manually insert a QC row under a selected parent row (legacy QCadd_Execute
  // parity) — stateless: posts the CURRENT full sequence, returns the recomputed
  // one (naming + same-type renumbering applied server-side).
  addQc: (testMethodId: string, parentSort: number, newQcTypeOid: string, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/add-qc`,
      { testMethodId, parentSort, newQcTypeOid, sequence }).then(r => unwrap(r.data)),

  // Remove a row from the sequence (legacy QCremove_Execute parity, minus the
  // no-op "canremove" check — see QcSequenceBuilder.RemoveQc).
  removeQc: (testMethodId: string, removeSort: number, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/remove-qc`,
      { testMethodId, removeSort, sequence }).then(r => unwrap(r.data)),

  // Drag-drop reorder (legacy DRAGROW parity) — server validates the drop against
  // each row's allowed Sort window and returns the resequenced list.
  moveRow: (testMethodId: string, dragSort: number, targetSort: number, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/move-row`,
      { testMethodId, dragSort, targetSort, sequence }).then(r => unwrap(r.data)),

  // "Add Dilution" context-menu action (legacy parity) — adds one R replicate to the
  // selected non-dilution row; server derives the name + placement and resequences.
  addDilution: (testMethodId: string, parentSort: number, sequence: QcSeqRow[]) =>
    api.post<Envelope<QcSeqRow[]>>(`${BASE}/add-dilution`,
      { testMethodId, parentSort, sequence }).then(r => unwrap(r.data)),
};
